import { useEffect, useRef, useState } from "react";
import { Handle, Position } from "@xyflow/react";
import { createPortal } from "react-dom";
import axios from "./../../utils/axiosInstance";
import { useDispatch, useSelector } from "react-redux";
import { ungroupNode, updateNode } from "../../utils/flowSlice";
import { BiLoaderCircle, BiImage } from "react-icons/bi";
import { TbZoomInArea, TbZoomOutArea } from "react-icons/tb";
import { FaRegObjectUngroup } from "react-icons/fa6";
import { useContext } from "react";
import { DarkModeContext } from "../../context/DarkModeContext";
import convertBlobToBase64 from "../../utils/convertBlobToBase64";
import toast from "react-hot-toast";

const ImageNode = ({ data, isConnectable }) => {
  const [isZoomed, setIsZoomed] = useState(false);
  const [title, setTitle] = useState(data.title || "Fetching the title");
  const [script, setScript] = useState(data.script || "");
  const [note, setNote] = useState(data.note || "");
  const [image, setImage] = useState(data.image || null);
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const nodes = useSelector((store) => store.flow.nodes);
  const currentNode = nodes.find((node) => node.id === data.id);
  const isGrouped = !!currentNode ? !!currentNode.parentId : false;

  const unGroup = () =>
    dispatch(
      ungroupNode({
        id: data.id,
      })
    );

  const getTitleFromImage = async (imageUrl) => {
    if (title !== "Fetching the title") return;
    setLoading(true);

    const resizeImage = async (base64Image, maxWidth, maxHeight) => {
      const img = new Image();
      img.src = base64Image;
      await new Promise((resolve) => {
        img.onload = resolve;
      });

      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      // Calculate new dimensions while maintaining aspect ratio
      let width = img.width;
      let height = img.height;
      if (width > maxWidth || height > maxHeight) {
        if (width / height > maxWidth / maxHeight) {
          width = maxWidth;
          height = Math.round((width * img.height) / img.width);
        } else {
          height = maxHeight;
          width = Math.round((height * img.width) / img.height);
        }
      }

      canvas.width = width;
      canvas.height = height;
      ctx.drawImage(img, 0, 0, width, height);

      // Convert to Base64
      const resizedBase64Image = canvas.toDataURL("image/jpeg", 0.8); // Adjust quality as needed
      return resizedBase64Image;
    };

    // Example usage
    const base64Image = await convertBlobToBase64(imageUrl);
    setImage(base64Image);
    const resizedBase64Image = await resizeImage(base64Image, 800, 600); // Resize to max 800x600
    const imageForOpenAI = resizedBase64Image.split(",")[1];

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASED_URL}/service/image`,
        {
          imageForOpenAI: imageForOpenAI,
        }
      );
      const tempTitle = response.data.tempTitle;
      const tempScript = response.data.tempScript;

      setLoading(false);
      setTitle(tempTitle);
      dispatch(
        updateNode({
          id: data.id,
          data: {
            ...data,
            script: tempScript,
            title: tempTitle,
            image: base64Image,
          },
        })
      );
    } catch (err) {
      console.log(err);
      if (err.status === 402) {
        toast.error(
          "You have run out of your credit. You need to upgrade your plan"
        );
      }
    }
  };

  useEffect(() => {
    getTitleFromImage(data.imageUrl);
  }, [data.imageUrl]);

  const handleZoomClick = () => {
    setIsZoomed(true); // Show the modal
  };

  const handleCloseModal = () => {
    setIsZoomed(false); // Hide the modal
  };

  const { darkMode } = useContext(DarkModeContext);
  const textAreaRef = useRef(null);
  const handleInputChange = (e) => {
    setNote(e.target.value);
    if (e.target.scrollHeight > 50) {
      e.target.style.height = `${e.target.scrollHeight}px`;
    }
    if (e.target.value === "") {
      e.target.style.height = `${50}px`;
    }
    dispatch(
      updateNode({
        id: data.id,
        data: {
          ...data,
          note: e.target.value,
        },
      })
    );
  };

  return (
    <div>
      <Handle
        type="source"
        position={Position.Right}
        isConnectable={isConnectable}
        style={{
          width: "20px", // Default width
          height: "20px", // Default height
          background: "white", // Default background color
          border: "2px solid #32b5e5", // Default border color
          borderRadius: "50%", // Circular shape
          position: "absolute",
          right: "-20px", // Ensure proper alignment
          // top: "50%", // Align vertically
          transform: "translateY(-50%)", // Center the handle
          display: "flex",
          alignItems: "center",
          lineHeight: "20px", // Match the handle's height for vertical centering
          fontSize: "12px", // Ensure text size fits
          justifyContent: "center",
          transition: "transform 0.2s, background-color 0.2s, right 0.2s", // Smooth hover transition
        }}
        onMouseEnter={(e) => {
          e.target.style.transform = "translateY(-50%) scale(1.5)"; // Keep alignment and scale
          e.target.style.background = "#32b5e5"; // Change background to blue
          e.target.style.color = "white"; // Set text color to white
          e.target.innerHTML = "+"; // Add "+" symbol inside
        }}
        onMouseLeave={(e) => {
          e.target.style.transform = "translateY(-50%) scale(1)"; // Reset scale while maintaining alignment
          e.target.style.background = "white"; // Reset background to white
          e.target.style.color = ""; // Clear the text color
          e.target.innerHTML = ""; // Remove "+" symbol
        }}
      />

      <div
        className={`flex flex-col gap-1 w-[320px] mx-auto ${
          darkMode ? "bg-[#eeeee6]" : "bg-[#080808]"
        }`}
        tabIndex={0}
      >
        {/* Title Section */}
        <div className="flex items-center justify-start space-x-2 px-3 py-2 bg-pink-500  text-white rounded-[8px]">
          {isGrouped && (
            <FaRegObjectUngroup
              size={"16"}
              color="white"
              className="hover:cursor-pointer"
              onClick={() => unGroup()}
            />
          )}

          {loading ? (
            <div className="flex items-center justify-start">
              <BiLoaderCircle
                size={"18"}
                className="loading-icon"
                color="white"
              />
              <h3 className="ml-2 w-52 flex justify-start text-white font-semibold text-sm">
                Fetching the title
              </h3>
            </div>
          ) : (
            <div className="flex items-center justify-start">
              <BiImage size={"18"} color="white" />
              <h3 className="ml-2 font-semibold text-xs text-left overflow-hidden text-nowrap overflow-ellipsis w-[230px] text-white">
                {title}
              </h3>
            </div>
          )}
        </div>

        {/* Image Section */}
        <div className="relative object-cover ">
          <img
            // src="https://placekitten.com/300/300"
            src={image || "AdobeStock_797148184_Preview.jpeg"}
            alt="Adorable Ginger Kitten"
            className="w-full h-[277px] object-cover rounded-[8px]"
          />
          {/* Zoom Icon */}
          <button
            className="absolute top-3 right-3 w-5 h-5 flex items-center justify-center bg-white text-white rounded-full  hover:bg-gray-300 transition-all focus:outline-none"
            onClick={handleZoomClick}
          >
            <TbZoomInArea size={13} color="#ec4899" />
          </button>
        </div>

        {/* Notes Section */}
        <textarea
          ref={textAreaRef}
          className={` nodrag w-full h-[36px] min-h-[50px] max-h-[120px] ${
            darkMode
              ? "bg-[#f5f8fa] border-neutral-300 text-black"
              : "bg-[#292929] border-[#292929] text-neutral-200"
          } text-sm font-sans  p-1.5 pr-3 rounded-[8px]  focus-within:outline-none resize-y `}
          placeholder={"Add notes for AI to use..."}
          value={note}
          onChange={(e) => handleInputChange(e)}
          rows={1}
        ></textarea>
      </div>

      {/* Full-Screen Modal */}
      {isZoomed &&
        createPortal(
          <div
            className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center"
            onClick={handleCloseModal} // Close the modal when clicking the background
          >
            <div
              className="relative bg-white rounded-lg"
              onClick={(e) => e.stopPropagation()} // Prevent background click from closing the modal
            >
              <img
                src={image || "AdobeStock_797148184_Preview.jpeg"}
                alt="Zoomed Adorable Ginger Kitten"
                className="max-w-[90vw] max-h-[90vh] rounded-lg object-contain"
              />
              <button
                className="absolute top-3 right-3 bg-gray-200 text-gray-800 rounded-full w-8 h-8 flex items-center justify-center  hover:bg-gray-300 transition-all"
                onClick={handleCloseModal}
              >
                <TbZoomOutArea size={17} />
              </button>
            </div>
          </div>,
          document.body // Render modal in the root of the DOM
        )}
    </div>
  );
};

export default ImageNode;
